import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import config from "../config/config";
import { Input } from "./ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "./ui/popover";

function buildRequestHeaders(headers) {
  return headers && Object.keys(headers).length ? headers : undefined;
}

function describeEntry(entry) {
  const variants = Number(entry?.variant_count || 0);
  const specializations = Number(entry?.specialization_count || 0);
  const parts = [];
  if (variants > 0) parts.push(`${variants} Varianten`);
  if (specializations > 0) parts.push(`${specializations} Spezialisierungen`);
  return parts.join(" · ");
}

export default function ProfessionSuggestField({
  value,
  onChange,
  placeholder = "z. B. Pflegefachkraft",
  className = "",
  headers = null,
  minChars = 2,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  const query = String(value || "").trim();
  const canSearch = query.length >= minChars;

  useEffect(() => {
    let cancelled = false;
    if (!canSearch) {
      setLoading(false);
      setError("");
      setResults([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get(`${config.API_BASE_URL}/api/wt/professions`, {
          params: { q: query, limit: 12 },
          headers: buildRequestHeaders(headers),
        });
        if (cancelled) return;
        const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setResults(rows);
        setOpen(true);
      } catch (_err) {
        if (cancelled) return;
        setResults([]);
        setError("Berufsvorschlaege konnten gerade nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canSearch, headers, query]);

  const helperText = useMemo(() => {
    if (!canSearch) return `Mindestens ${minChars} Zeichen eingeben, z. B. Pflege.`;
    if (loading) return "Suche laeuft...";
    if (error) return error;
    if (!results.length) return "Keine passenden Berufe gefunden.";
    return `${results.length} Berufsvorschlaege gefunden.`;
  }, [canSearch, error, loading, minChars, results.length]);

  return (
    <Popover open={open && (canSearch || results.length > 0)} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            value={value || ""}
            onChange={(event) => {
              onChange(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              if (canSearch || results.length) setOpen(true);
            }}
            placeholder={placeholder}
            className={className}
            autoComplete="off"
          />
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-2"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div className="px-2 pb-2 text-xs text-slate-400">{helperText}</div>
        <div className="max-h-64 overflow-y-auto">
          {results.map((entry) => (
            <button
              key={entry.family}
              type="button"
              className="flex w-full flex-col rounded-md px-3 py-2 text-left transition hover:bg-white/5"
              onClick={() => {
                onChange(entry.family);
                setOpen(false);
              }}
            >
              <span className="text-sm font-medium text-slate-100">{entry.family}</span>
              <span className="text-xs text-slate-400">{describeEntry(entry) || "Berufsfamilie"}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
